import React from 'react';
import { FormProvider as ReactHookFormProvider } from 'react-hook-form';
import type { UseFormReturn, FieldValues, SubmitHandler, SubmitErrorHandler, UseFormReset, UseFormSetValue, Mode, FieldPath } from 'react-hook-form';
import type { SlotsToClasses } from '@heroui/react';
import { Button } from '@heroui/react';
import type { ButtonProps } from '@heroui/react';
import { FormProvider } from './context';
import { debug } from '../client';
import { useForm } from './hook';

export type FormSlots = "base" | "footer";
export type FormFooterConfig = {
    resetProps?: Omit<ButtonProps, "children">;
    resetText?: React.ReactNode;
    submitProps?: Omit<ButtonProps, "children">;
    submitText?: React.ReactNode;
    hiddenReset?: boolean;
    onReset?: () => void;
};

export interface FormInstance<TFieldValues extends FieldValues = FieldValues> extends UseFormReturn<TFieldValues> {
  // 基础方法
  getFieldValue: (name: FieldPath<TFieldValues>) => any;
  getFieldsValue: () => TFieldValues;
  setFieldValue: (name: FieldPath<TFieldValues>, value: any) => void;
  setFieldsValue: (values: Partial<TFieldValues>) => void;
  resetFields: () => void;
  
  // 验证方法
  validateFields: () => Promise<boolean>;
  setFieldError: (name: FieldPath<TFieldValues>, error: any) => void;
  clearFieldErrors: (names?: FieldPath<TFieldValues>[]) => void;
}

export interface FormAction<TFieldValues extends FieldValues = FieldValues> {
    submit: () => void;
    reset: UseFormReset<TFieldValues>;
    setFieldValue: UseFormSetValue<TFieldValues>;
    setFieldValues: (values: Partial<TFieldValues>) => void;
    getFieldValues: () => TFieldValues;
}

export interface FormProps<TFieldValues extends FieldValues = FieldValues> extends Omit<React.DetailedHTMLProps<React.FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>, 'method'> {
    classNames?: SlotsToClasses<FormSlots>;
    footer?: false | FormFooterConfig;
    mode?: Mode;
    watchNames?: FieldPath<TFieldValues> | FieldPath<TFieldValues>[];
    onFinish?: SubmitHandler<TFieldValues>;
    onFinishError?: SubmitErrorHandler<TFieldValues>;
    form?: FormInstance<TFieldValues>;
}
export function Form<TFieldValues extends FieldValues = FieldValues>(props: FormProps<TFieldValues>) {
    const { mode = 'all', watchNames, form, ...restProps } = props;
    const methods = form || useForm<TFieldValues>({ mode });

    return <FormWithMethods {...restProps} method={methods} watchNames={watchNames} />;
}

export interface FormWithMethodsProps<TFieldValues extends FieldValues = FieldValues> extends FormProps<TFieldValues> {
    method: FormInstance<TFieldValues>;
}

export function FormWithMethods<TFieldValues extends FieldValues = FieldValues>({
    method,
    classNames,
    footer,
    watchNames,
    onFinish,
    onFinishError,
    children,
    ...formProps
}: FormWithMethodsProps<TFieldValues>) {
    const { handleSubmit, reset, formState, watch } = method;
    const { isSubmitting } = formState;

    // 实现watchNames功能：监听指定字段，控制按钮状态
    const watchedValues = watchNames ? (Array.isArray(watchNames)
        ? watch(watchNames as readonly FieldPath<TFieldValues>[])
        : watch(watchNames as FieldPath<TFieldValues>)
    ) : undefined;

    const isDisabled = React.useMemo(() => {
        if (!watchNames || watchedValues === undefined) return false;

        debug.log('watchNames debug:', { watchNames, watchedValues, isArray: Array.isArray(watchNames) });

        // 检查是否为数组或单个值
        if (Array.isArray(watchNames)) {
            // 多个字段监听：所有字段都必须有有效值
            if (Array.isArray(watchedValues)) {
                const hasAllValues = watchedValues.every(value => {
                    // 检查值是否为有效（非空字符串、非 null、非 undefined）
                    return value !== '' && value !== null && value !== undefined;
                });
                const shouldDisable = !hasAllValues;
                debug.log('Multi-field check:', { watchedValues, hasAllValues, shouldDisable });
                return shouldDisable;
            }
            // 如果watchNames是数组但watchedValues不是数组，说明watch函数可能有问题
            // 手动获取每个字段的值
            const values = watchNames.map(name => watch(name));
            const hasAllValues = values.every(value => {
                return value !== '' && value !== null && value !== undefined;
            });
            const shouldDisable = !hasAllValues;
            debug.log('Multi-field fallback check:', { values, hasAllValues, shouldDisable });
            return shouldDisable;
        } else {
            // 单个字段监听：字段必须有有效值  
            const value = watchedValues as any;
            const shouldDisable = value === '' || value === null || value === undefined;
            debug.log('Single field check:', { value, shouldDisable });
            return shouldDisable;
        }
    }, [watchNames, watchedValues]);

    const renderFooter = () => {
        if (footer === false) return null;

        const {
            resetProps = {},
            resetText = '重置',
            submitProps = {},
            submitText = '提交',
            hiddenReset = false,
            onReset,
        } = footer || {};

        return (
            <div className={`form-footer flex gap-2 pt-4 ${classNames?.footer || ''}`}>
                {!hiddenReset && (
                    <Button
                        type="button"
                        variant="flat"
                        isDisabled={isDisabled}
                        {...resetProps}
                        onPress={() => {
                            reset();
                            onReset?.();
                        }}
                    >
                        {resetText}
                    </Button>
                )}

                <Button
                    type="submit"
                    color="primary"
                    isLoading={isSubmitting}
                    isDisabled={isDisabled}
                    {...submitProps}
                >
                    {submitText}
                </Button>
            </div>
        );
    };

    return (
        <ReactHookFormProvider {...method}>
            <FormProvider value={method}>
                <form
                    {...formProps}
                    onSubmit={handleSubmit(onFinish || (() => { }), onFinishError)}
                    className={`form-base space-y-4 ${classNames?.base || ''}`}
                >
                    {children}
                    {renderFooter()}
                </form>
            </FormProvider>
        </ReactHookFormProvider>
    );
}
